import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const HERO_BG =
  "https://customer-assets.emergentagent.com/job_madre-gourmet/artifacts/8xo3ix6f_Gemini_Generated_Image_ld6klgld6klgld6k.png";

export default function HeroSection({ onShopNow }) {
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={sectionRef}
      data-testid="hero-section"
      className="relative h-screen overflow-hidden"
    >
      {/* Cinematic background with parallax */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 z-0"
      >
        <img
          src={HERO_BG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-center"
          loading="eager"
          data-testid="hero-bg-image"
        />
        {/* Left-to-right gradient overlay for text readability */}
        <div
          className="absolute inset-0 hidden lg:block"
          style={{
            background:
              "linear-gradient(to right, rgba(10,10,10,0.88) 0%, rgba(10,10,10,0.65) 40%, rgba(10,10,10,0.2) 70%, transparent 100%)",
          }}
        />
        {/* Mobile/tablet: stronger full overlay */}
        <div
          className="absolute inset-0 lg:hidden"
          style={{
            background:
              "linear-gradient(to bottom, rgba(10,10,10,0.5) 0%, rgba(10,10,10,0.75) 40%, rgba(10,10,10,0.6) 70%, rgba(10,10,10,0.85) 100%)",
          }}
        />
        {/* Bottom fade for section transition */}
        <div
          className="absolute bottom-0 left-0 right-0 h-40"
          style={{
            background:
              "linear-gradient(to top, #1A1A1A 0%, transparent 100%)",
          }}
        />
        {/* Noise texture */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "url('https://grainy-gradients.vercel.app/noise.svg')",
          }}
        />
      </motion.div>

      {/* Content — left-aligned over the dark gradient side */}
      <div className="relative z-10 h-full flex items-center">
        <motion.div
          style={{ y: textY, opacity: textOpacity }}
          className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 w-full"
        >
          <div className="max-w-xl lg:max-w-2xl">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#A3A3A3] mb-6"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Salsas Artesanales Premium de México
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.9, ease: "easeOut" }}
              className="text-5xl sm:text-6xl lg:text-8xl font-bold tracking-tight leading-[0.9] mb-8"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              <span className="gold-text">Salsas</span>
              <br />
              <span className="gold-text">con Madre</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.8 }}
              className="text-base md:text-lg text-[#c0bfba] max-w-md mb-10 leading-relaxed"
            >
              Hechas a mano en pequeños lotes con ingredientes 100% naturales del campo mexicano. Sin conservadores, sin químicos. Puro sabor, pura tradición.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <button
                data-testid="hero-shop-now-btn"
                onClick={onShopNow}
                className="bg-[#D4AF37] text-[#1A1A1A] px-10 py-4 font-semibold uppercase tracking-wider text-sm hover:bg-[#E5C158] transition-all duration-300 gold-glow"
              >
                Ver la Colección
              </button>
              <button
                data-testid="hero-story-btn"
                onClick={() => {
                  const el = document.querySelector("#story");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="btn-outline-gold border border-[#D4AF37] text-[#D4AF37] px-10 py-4 font-semibold uppercase tracking-wider text-sm transition-all duration-300"
              >
                Nuestra Historia
              </button>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3]/70">
            Desliza
          </span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{
              repeat: Infinity,
              duration: 1.5,
              ease: "easeInOut",
            }}
            className="w-[1px] h-8 bg-[#D4AF37]/50"
          />
        </motion.div>
      </div>
    </section>
  );
}
